import "./TestOrderFiltersComponent.scss";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import CommonService from "../../../shared/services/common.service";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import formValidationRules from "../../../shared/services/validation.service";
import * as Yup from "yup";
import {ACL_RULES} from "../../../constants/ACLRules";
import {IAPIResponseType} from "../../../shared/models/api.model";

interface TestOrderFiltersComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    clearAllFilters: () => void;
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    })
}, [['startDate', 'endDate']]);

const TestOrderFiltersComponent = (props: TestOrderFiltersComponentProps) => {
    const {filtersData, onFiltersApply, clearAllFilters} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const {
        facilityListLite,
        physicianListLite,
        testOrderStatusList,
    } = useSelector((state: IRootReducerState) => state.staticData);
    const [locationList, setLocationList] = useState([]);
    const [isLocationListLoading, setIsLocationListLoading] = useState(false);
    const [isLocationListLoadingFailed, setIsLocationListLoadingFailed] = useState(false);
    const [isLocationListLoaded, setIsLocationListLoaded] = useState(false);

    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {
            ...values
        }
        payload.facilityIds = payload.facilityObjs.map((item: any) => item.id);
        payload.locationIds = payload.locationObjs.map((item: any) => item.id);
        payload.physicianIds = payload.physicianObjs.map((item: any) => item.id);
        payload.statuses = payload.statusesObjs.map((item: any) => item.code);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : "";
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : "";

        onFiltersApply(payload);
    }, [onFiltersApply]);
    const resetLocationList = useCallback(() => {
        setLocationList([]);
        formikRef?.current?.setFieldValue('locationObjs', []);
    }, [])
    const getLocationList = useCallback((selectedFacilities: any[]) => {
        const facilityIds = selectedFacilities.map((facility: any) => facility?.id);
        const payload = {facilityIds: facilityIds, isActive: true};
        setIsLocationListLoading(true);
        setIsLocationListLoadingFailed(false);
        setIsLocationListLoaded(false);
        let locationList: any = [];
        resetLocationList();
        CommonService._location.getFacilityLocation(payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    locationList = response.data;
                }
                setLocationList(locationList);
                setIsLocationListLoading(false);
                setIsLocationListLoadingFailed(false);
                setIsLocationListLoaded(true);
            })
            .catch((error: any) => {
                setIsLocationListLoading(false);
                setIsLocationListLoaded(false);
                setIsLocationListLoadingFailed(true);
            });
    }, [resetLocationList]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    return (
        <div className={'test-order-filters-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm();
                             clearAllFilters();
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <Formik
                        initialValues={dataFiltersInitialState}
                        enableReinitialize={true}
                        innerRef={formikRef}
                        onSubmit={onFiltersApplyHandler}
                        validationSchema={validationSchema}
                    >
                        {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [values, errors, validateForm]);

                            return (
                                <Form noValidate={true} className={"patient-filters-holder"}>
                                    <div className="t-form-controls-wrapper">
                                        {
                                            ACL_RULES.TEST_ORDER_FACILITY_FILTER.includes(currentUser?.roleObj?.code) &&
                                            <Field name={'facilityObjs'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            fullWidth={true}
                                                            label={'Facility Name'}
                                                            multiple={true}
                                                            data={facilityListLite}
                                                            valueKey={"id"}
                                                            placeholder={'Select Facility Name'}
                                                            formikField={field}
                                                            getOptionLabel={(option => (option.name) || '')}
                                                            onUpdate={(value) => {
                                                                getLocationList(value);
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        }
                                        {
                                            ACL_RULES.TEST_ORDER_LOCATION_FILTER.includes(currentUser?.roleObj?.code) &&
                                            <Field name={'locationObjs'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            fullWidth={true}
                                                            label={'Location Name'}
                                                            multiple={true}
                                                            data={locationList}
                                                            isDataLoading={isLocationListLoading}
                                                            isDataLoaded={isLocationListLoaded}
                                                            disabled={values?.facilityObjs?.length < 1}
                                                            isDataLoadingFailed={isLocationListLoadingFailed}
                                                            valueKey={"id"}
                                                            noDataMessage={'No Location found for facility'}
                                                            placeholder={'Select Location Name'}
                                                            formikField={field}
                                                            getOptionLabel={(option => (option.name) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        }

                                        <Field name={'physicianObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Physician Name'}
                                                        multiple={true}
                                                        data={physicianListLite}
                                                        valueKey={"id"}
                                                        placeholder={'Select Provider'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.firstName + ' ' + option.lastName) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'statusesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Select Test Order Status'}
                                                        multiple={true}
                                                        data={testOrderStatusList}
                                                        valueKey={"code"}
                                                        placeholder={'Select Test Order Status'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (option.title)}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'startDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Start Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'endDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'End Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="drawer-actions form-action-section">
                                        <ButtonComponent variant={"contained"}
                                                         fullWidth={true}
                                                         color={"primary"}
                                                         type={"submit"}
                                                         disabled={!isValid || !dirty}
                                        >
                                            Apply
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }
                        }
                    </Formik>
                </div>
            </>

        </div>
    );

};

export default TestOrderFiltersComponent;
